import { MsalService } from "@azure/msal-angular";
import {
	AccountInfo,
	AuthenticationResult,
	BrowserAuthError,
	InteractionRequiredAuthError,
	ServerError,
} from "@azure/msal-browser";
import { environment } from "@env/environment";
import { isTeamsWindow } from "@env/msal";
import * as Sentry from "@sentry/angular";
import { Logger, Scope } from "@timeghost/web-api";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { toPromise } from "./_helpers/promise";
import { queryParams, stringify } from "./_helpers/utils";
import { EMAIL_REGEX } from "./_validators/custom-validators";
import { InitServiceArgs } from "./app-load-handler";
import { TeamsService } from "./services/teams.service";

const log = new Logger("AppAuth-Handler");
const AUTH_REDIRECT_ROUTE_KEY = "tg.auth.redirectRoute";
/** Prod Teams interactive login UI (static HTML + user-gesture authenticate()). */
const TEAMS_AUTH_BUTTON_PATH = "/teams-auth-button.html";
let teamsAuthNavigationStarted = false;
const requireLoginErrors = [
	"interaction_required",
	"login_required",
	"invalid_grant",
	"no_account_error",
	"consent_required",
	"no_tokens_found",
	"invalid_request",
	"monitor_window_timeout",
	"redirect_in_iframe",
] as const;
type RequireLoginError = (typeof requireLoginErrors)[number];
const allowedPrompts = ["select_account", "consent"] as const;
type AllowedPrompt = (typeof allowedPrompts)[number];

export function hasRequireLoginError(code: StringLiteral<RequireLoginError>) {
	return code && requireLoginErrors.find((c) => c === code.toLowerCase());
}

/** Graph + OpenID + custom API scope used by MsalInterceptor / backend. */
function getAuthScopes(): string[] {
	// offline_access: silent renew in browser + Teams iframe (3P cookies unreliable).
	return [
		Scope.Me,
		Scope.OpenId,
		Scope.Profile,
		Scope.Users,
		Scope.People,
		environment.adalConfig.apiAccessUrl,
		"offline_access",
	].filter(Boolean) as string[];
}

function persistTeamsAuthLocalStorage(scopes: string[]) {
	localStorage.teams_auth_scopes = scopes.toString();
	localStorage.teams_auth_clientId = environment.adalConfig.clientId;
	localStorage.teams_auth_scope_url = environment.adalConfig.apiAccessUrl;
}

function checkAndSetActiveAccount(msal: MsalService) {
	let activeAccount = msal.instance.getActiveAccount();

	if (!activeAccount && msal.instance.getAllAccounts().length > 0) {
		const accounts = msal.instance.getAllAccounts();
		msal.instance.setActiveAccount((activeAccount = accounts[0]));
	}
	return activeAccount;
}

function getCurrentRoute() {
	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function cachePreAuthRoute() {
	const params = Object.fromEntries(new URL(location.href).searchParams) || {};
	if (hasAuthTransitionParams(params)) {
		return;
	}
	localStorage.setItem(AUTH_REDIRECT_ROUTE_KEY, getCurrentRoute());
}

function getRestoreRoute(params: Record<string, string>) {
	const cachedRoute = localStorage.getItem(AUTH_REDIRECT_ROUTE_KEY);
	const queryRoute = params?.redirect ? decodeURIComponent(params.redirect) : null;
	const route = queryRoute || cachedRoute;
	if (!route) {
		return null;
	}
	if (!route.startsWith("/")) {
		return null;
	}
	return route;
}

function hasAuthTransitionParams(params: Record<string, string>) {
	const authParams = ["code", "state", "error", "error_description", "session_state", "client_info", "result"];
	return authParams.some((key) => !!params?.[key]);
}

function restorePreAuthRoute(params: Record<string, string>) {
	if (!hasAuthTransitionParams(params)) {
		return;
	}
	const route = getRestoreRoute(params);
	localStorage.removeItem(AUTH_REDIRECT_ROUTE_KEY);
	if (!route || route === getCurrentRoute()) {
		return;
	}
	window.history.replaceState(window.history.state, "", route);
}

const initFinish = new Subject<void>();
const closeInit = () => (initFinish.next(), initFinish.complete());

function notifyTeamsAppLoaded(teams?: TeamsService) {
	try {
		teams?.getInstance()?.app.notifyAppLoaded();
	} catch (err) {
		log.debug("notifyAppLoaded failed", err);
	}
}

/**
 * Teams tab must not use loginRedirect (iframe → redirect_in_iframe).
 * Prod path: static teams-auth-button.html → user click → authentication.authenticate() popup.
 */
async function teamsInteractiveLogin(
	teams: TeamsService,
	options?: { prompt?: AllowedPrompt }
): Promise<"teams_auth_page"> {
	const scopes = getAuthScopes();
	persistTeamsAuthLocalStorage(scopes);
	notifyTeamsAppLoaded(teams);
	cachePreAuthRoute();

	if (window.location.pathname === TEAMS_AUTH_BUTTON_PATH || teamsAuthNavigationStarted) {
		return "teams_auth_page";
	}
	teamsAuthNavigationStarted = true;

	log.info("redirect to teams auth button page");
	const loginUrl = new URL(`${window.location.origin}${TEAMS_AUTH_BUTTON_PATH}`);
	loginUrl.searchParams.set("in_teams", "1");
	if (options?.prompt) {
		loginUrl.searchParams.set("type", options.prompt);
	}
	// location.replace stays in the Teams iframe. Synthetic <a>.click() is intercepted
	// and opened in the desktop browser, which then re-runs Teams auth (window loop).
	window.location.replace(loginUrl.toString());
	return "teams_auth_page";
}

async function login(
	msal: MsalService,
	teams: TeamsService,
	options?: { consentRequired?: boolean; prompt?: AllowedPrompt }
) {
	const persistQP = queryParams();
	cachePreAuthRoute();
	localStorage.qpcache = stringify({ ...persistQP, _expi: new Date().toISOString() });

	const isTeams = teams.isInitialized() || isTeamsWindow();
	const needConsent = persistQP.prompt === "consent" || options?.consentRequired || options?.prompt === "consent";
	const prompt = needConsent ? "consent" : options?.prompt;

	if (isTeams) {
		return teamsInteractiveLogin(teams, { prompt });
	}

	return msal
		.loginRedirect({
			redirectStartPage: `${window.location.origin}/?result=login`,
			scopes: getAuthScopes(),
			...(prompt ? { prompt } : {}),
		})
		?.toPromise();
}

function ssoErrorHandler(err: InteractionRequiredAuthError | BrowserAuthError) {
	if (hasRequireLoginError(err.errorCode)) return true;
	return false;
}

function setMsalSentryTags(account?: AccountInfo) {
	if (account && EMAIL_REGEX.test(account.username)) {
		Sentry.setTags({
			"user.id": account.localAccountId,
			"user.email": account.username,
		});
		if (account.tenantId) {
			Sentry.setTags({
				"user.tenantId": account.tenantId,
			});
		}
	}
}

async function selectAccount(msal: MsalService, teams: TeamsService) {
	const persistQP = queryParams();
	cachePreAuthRoute();
	localStorage.qpcache = stringify({ ...persistQP, _expi: new Date().toISOString() });

	const isTeams = teams.isInitialized() || isTeamsWindow();
	if (isTeams) {
		return teamsInteractiveLogin(teams, { prompt: "select_account" });
	}

	return msal
		.loginRedirect({
			redirectStartPage: `${window.location.origin}/?result=select_account`,
			scopes: getAuthScopes(),
			prompt: "select_account",
		})
		?.toPromise();
}

async function trySsoSilent(msal: MsalService, teams: TeamsService, options?: { consentRequired?: boolean }) {
	try {
		// Never pass interactive prompt to ssoSilent — silent APIs reject consent/select_account.
		const ssoResult = await toPromise(
			msal.ssoSilent({
				scopes: [Scope.Me],
			})
		);
		log.debug("sso result:", ssoResult);
		return ssoResult;
	} catch (err: any) {
		log.error("sso err:", err);
		if (!ssoErrorHandler(err)) {
			return null;
		}
		// Cached account → caller tries acquireTokenSilent first (Teams iframe friendly).
		if (checkAndSetActiveAccount(msal) && !options?.consentRequired) {
			return null;
		}
		return login(msal, teams, {
			consentRequired: options?.consentRequired || err.errorCode === "consent_required",
		});
	}
}

async function acquireMeToken(msal: MsalService, account?: AccountInfo) {
	const tokenResult = await toPromise(
		msal.acquireTokenSilent({
			...(account ? { account } : {}),
			scopes: [Scope.Me],
		})
	);
	if (tokenResult?.account) {
		setMsalSentryTags(tokenResult.account);
	}
	return tokenResult;
}

export const authorizeMsal = async ({
	msal,
	teams,
	http,
}: {
	msal: InitServiceArgs["msal"];
	teams: InitServiceArgs["teamsService"];
	http: InitServiceArgs["http"];
}) => {
	const params = Object.fromEntries(new URL(location.href).searchParams) || {};
	const isTeams = teams.isInitialized() || isTeamsWindow();
	log.debug("init Auth", "teams:", isTeams);

	if (isTeams) {
		persistTeamsAuthLocalStorage(getAuthScopes());
	}

	let account = checkAndSetActiveAccount(msal);
	if (!environment.production) window.msal = msal.instance;
	setMsalSentryTags(account);

	const forceConsent = params?.type === "consent" || params?.prompt === "consent";
	const accountSelected = params?.result === "select_account";
	const forceSelectAccount = !accountSelected && params?.type === "select_account";

	return await toPromise(msal.handleRedirectObservable().pipe(takeUntil(initFinish)))
		.then(async (auth: AuthenticationResult | null) => {
			if (forceSelectAccount) {
				return await selectAccount(msal, teams);
			}

			if (!auth) {
				const ssoResult = await trySsoSilent(msal, teams, {
					consentRequired: forceConsent,
				});
				if (ssoResult && typeof ssoResult === "object" && "account" in ssoResult && ssoResult.account) {
					account = ssoResult.account;
					msal.instance.setActiveAccount(account);
				} else if (ssoResult === "teams_auth_page") {
					return ssoResult;
				} else {
					account = checkAndSetActiveAccount(msal);
				}

				if (!account) {
					return await login(msal, teams, {
						consentRequired: forceConsent,
					});
				}
				return await acquireMeToken(msal, account);
			}

			msal.instance.setActiveAccount(auth.account);
			return await acquireMeToken(msal);
		})
		.then((data) => {
			if (data === "teams_auth_page") {
				closeInit();
				notifyTeamsAppLoaded(teams);
				return data;
			}
			restorePreAuthRoute(params);
			closeInit();
			try {
				teams.getInstance()?.app.notifySuccess();
			} catch (err) {
				log.debug("notifySuccess failed", err);
			}
			return data;
		})
		.catch(async (err: any) => {
			if (
				err instanceof InteractionRequiredAuthError ||
				err instanceof BrowserAuthError ||
				err instanceof ServerError
			) {
				if (err.errorCode && hasRequireLoginError(err.errorCode)) {
					notifyTeamsAppLoaded(teams);
					return await login(msal, teams, {
						consentRequired: err.errorCode === "consent_required" || forceConsent,
					});
				}
			}
			return await Promise.reject(err);
		});
};

const initialize = async ({ msal, teamsService, http }: InitServiceArgs) => {
	return await authorizeMsal({ msal, teams: teamsService, http });
};
export default initialize;
